/**
 * @author Jersson Stiven Morales Alza
 * @Copyright (c) Infotrack. All rights reserved.
 */
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useProveedor } from '@infotrack/presentacion-componentes/proveedorEstado';
import Texto from '@infotrack/presentacion-componentes/texto';
import { TipoFormulario } from '@infotrack/presentacion-transversales/interfacesComunes';
import { useInputState } from '@infotrack/presentacion-utilitarios/hooks';
import { manejadorDialogoGlobal } from '@infotrack/presentacion-utilitarios/manejadoresComponentes';
import IMunicipio from 'Infotrack@Modelos/administracion/municipio/entidades/municipio';
import IClientes from 'Infotrack@Modelos/smartStock/clientes/entidades/clientes';
import TiposClientes from 'Infotrack@Modelos/smartStock/tiposClientes/entidades/tiposClientesEnum';
import { IEstadoGlobal } from 'Infotrack@Reductores/interfacesReductores';
import React from 'react';
import { useSelector } from 'react-redux';
import { connect } from 'react-redux';
import ClientesCM from '../controladorModelo/clientesCM';
import { IFormularioClientesCVProps, IInputsClientes, IValidacionesCliente } from '../interfaces';
import { acciones, IEstadoClientes } from '../reductorClientes';
import FormularioClientesVista from '../vista/formularioClientesVista';

const estadoInicial: IInputsClientes = {
    Ciudad: null,
    CodigoBarras: '',
    Correo: '',
    DescripcionCliente: '',
    DescripcionSucursal: '',
    Direccion: '',
    DiasVencimiento: '',
    Documento: '',
    Estado: true,
    NombreContacto: '',
    Principal: false,
    Telefono: '',
    TipoClienteId: '',
    CodigoInternoCliente: ''
};
const validacionesIniciales: IValidacionesCliente = {
    esJuridico: false,
    esNatural: false,
    esPrincipal: false,
    esSucursal: false,
};
const clientesCM = new ClientesCM();

const FormularioClientesCV: React.FunctionComponent<IFormularioClientesCVProps> = ({ IdEmpresa }) => {
    const idAgencia = useSelector((estado: IEstadoGlobal) => estado.estadoAutenticacion.agenciaActual!.IdAgencia);
    const [{ estadoFormulario, tipoFormulario, entidadClientes, entidadesClientes }, dispatch] = useProveedor<
        IEstadoClientes
    >();
    const { alCambiarValor, setValor, reiniciar, valor } = useInputState<IInputsClientes>(estadoInicial);
    const [validaciones, setValidaciones] = React.useState(validacionesIniciales);

    const theme = useTheme();
    const esMovil = useMediaQuery(theme.breakpoints.down('sm'));

    React.useEffect(() => {
        if (entidadClientes && tipoFormulario !== TipoFormulario.Creacion) {
            const municipio = entidadesClientes.municipios.find(
                ({ IdMunicipio }) => IdMunicipio === entidadClientes.CiudadId
            );
            setValor({ ...(entidadClientes as any), Ciudad: municipio });
            //validarClienteAEditar({ ...(entidadClientes as any), Ciudad: municipio });
        } else reiniciar();
    }, [entidadClientes]);

    React.useEffect(() => {
        validarTipoClienteInactivo();
    }, [valor]);

    const validarTipoClienteInactivo = () => {
        if (
            tipoFormulario === TipoFormulario.Edicion &&
            entidadesClientes.tiposClientes.find(
                (x) => x.TipoClienteId === parseInt(valor.TipoClienteId) && x.Estado === 0
            )
        ) {
            setValor({ ...valor, TipoClienteId: '0' });
        }
    };

    const cerrarFormulario = () => {
        dispatch({ type: acciones.CAMBIAR_ESTADO_FORMULARIO, payload: null });
        setValidaciones(validacionesIniciales);
    };

    const validarClienteAEditar = (clienteAEditar: IInputsClientes) => {
        const esNatural =
            entidadesClientes.tiposClientes.find(
                ({ TipoClienteId }) => TipoClienteId === Number(clienteAEditar.TipoClienteId)
            )!.TipoClienteDescripcion === TiposClientes.Natural;
        if (esNatural) {
            setValidaciones({ ...validaciones, esNatural });
            setValor({ ...clienteAEditar, Principal: true });
        } else {
            setValidaciones({
                ...validaciones,
                esJuridico: true,
                esPrincipal: clienteAEditar.Principal,
                esSucursal: !clienteAEditar.Principal,
            });
        }
    };

    const alCambiarValorCliente = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { checked, name, value } = e.target as EventTarget & HTMLInputElement;
        if (name === 'Principal' && checked) setValor({ ...valor, DescripcionSucursal: '', Principal: checked });
        else alCambiarValor(e);
    };

    const validarClienteCreacion = () => {
        clientesCM.validarCreacionCliente(mapearDatos()).then((cliente) => {
            if (cliente && cliente.Documento) {
                setValidaciones({ ...validaciones, esJuridico: true, esPrincipal: false });
                setValor({
                    ...valor,
                    DescripcionCliente: cliente.DescripcionCliente,
                    NombreContacto: cliente.NombreContacto,
                    Telefono: cliente.Telefono,
                });
            } else if (cliente) {
                setValidaciones({ ...validaciones, esPrincipal: cliente.Principal });
                setValor({ ...valor, Principal: cliente.Principal });
            }
        });
    };

    const confirmarCreacionEdicionClientes = () => {
        if (clientesCM.validarEntidad(mapearDatos())) {
            manejadorDialogoGlobal({
                accionCancelar: () => manejadorDialogoGlobal({ estado: false }),
                accionConfirmar: crearEditarClientes,
                estado: true,
                mensaje: (
                    <Texto
                        id={
                            tipoFormulario === TipoFormulario.Creacion
                                ? 'alerta.confirmacionCreacion'
                                : 'alerta.confirmacionEdicion'
                        }
                    />
                ),
                mostrarCancelar: true,
                tipoDialogo: 'Advertencia',
                titulo: <Texto id="titulo.advertencia" />,
            });
        }
    };

    const crearEditarClientes = () => {
        manejadorDialogoGlobal({ estado: false });
        if (tipoFormulario === TipoFormulario.Creacion) {
            clientesCM.crearClientes(mapearDatos()).then((resultado) => {
                if (resultado) {
                    cerrarFormulario();
                    dispatch({ type: acciones.RECARGAR_TABLA });
                }
            });
        } else {
            clientesCM.editarClientes(mapearDatos()).then((resultado) => {
                if (resultado) {
                    cerrarFormulario();
                    dispatch({ type: acciones.RECARGAR_TABLA });
                }
            });
        }
    };

    const mapearDatos = (): IClientes => {
        const { DiasVencimiento, Estado, TipoClienteId, CodigoInternoCliente } = valor;
        return {
            ...valor,
            CiudadId: Number(valor.Ciudad ? valor.Ciudad.IdMunicipio : 0),
            DiasVencimiento: DiasVencimiento ? Number(DiasVencimiento) : undefined,
            TipoClienteId: Number(TipoClienteId),
            Estado: Estado ? 1 : 0,
            EmpresaId: IdEmpresa,
            AgenciaId: idAgencia,
            CodigoInternoCliente: CodigoInternoCliente
        };
    };

    const alCambiarMunicipio = (nuevoMunicipio: IMunicipio) => {
        setValor({ ...valor, Ciudad: nuevoMunicipio });
    };

    return (
        <FormularioClientesVista
            accionCrearEditar={confirmarCreacionEdicionClientes}
            alCambiarMunicipio={alCambiarMunicipio}
            cambiarInfoFomulario={alCambiarValorCliente}
            cerrarFormulario={cerrarFormulario}
            clientes={valor}
            entidadesCliente={entidadesClientes}
            esMovil={esMovil}
            estadoFormulario={estadoFormulario}
            tipoFormulario={tipoFormulario}
            validaciones={validaciones}
            validarCliente={validarClienteCreacion}
        />
    );
};

const estadoAPropiedades = ({ estadoAutenticacion: { usuarioInformacion } }: IEstadoGlobal) => ({
    IdEmpresa: usuarioInformacion!.IdEmpresa,
});

export default connect<IFormularioClientesCVProps, null, any, IEstadoGlobal>(
    estadoAPropiedades,
    null
)(FormularioClientesCV);
